import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
// Self-hosted fonts. Previously loaded from the Google Fonts CDN via a
// <link> in index.html, which the production CSP blocks (style-src/font-src
// omit googleapis/gstatic). With the terminal font missing, xterm.js
// measured a fallback metric and mis-sized the grid, rendering full-screen
// TUIs (OpenCode) off-screen. Bundling serves them from 'self' — CSP-clean
// and offline-safe. Weights mirror the old CDN request.
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "@fontsource/dm-sans/400.css";
import "@fontsource/dm-sans/500.css";
import "@fontsource/dm-sans/600.css";
import "@fontsource/dm-sans/700.css";
import "@fontsource/instrument-serif/400.css";
import "@fontsource/instrument-serif/400-italic.css";
import "flexlayout-react/style/dark.css";
import "./styles/flexlayout-overrides.css";
import "./index.css";
import { configureMonacoLoader } from "./lib/monacoLoader";

// Point Monaco's loader at our self-hosted runtime before any Editor mounts.
// The default CDN URL fails in Tauri offline / under strict CSP and leaves
// the file viewer stuck on "Loading...".
configureMonacoLoader();

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// PWA update system (web builds only)
if (import.meta.env.VITE_BUILD_TARGET === 'web' && 'serviceWorker' in navigator) {
  const UPDATE_CHECK_INTERVAL = 3 * 60 * 1000; // Check every 3 minutes

  // Show a brief toast notification
  function showUpdateToast(message: string) {
    const toast = document.createElement('div');
    toast.textContent = message;
    Object.assign(toast.style, {
      position: 'fixed',
      bottom: '1.5rem',
      left: '50%',
      transform: 'translateX(-50%)',
      background: '#1c1917',
      color: '#f5f5f4',
      border: '1px solid #44403c',
      padding: '0.75rem 1.5rem',
      borderRadius: '0.75rem',
      fontSize: '0.875rem',
      fontFamily: 'DM Sans, sans-serif',
      zIndex: '9999',
      boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
      opacity: '0',
      transition: 'opacity 0.3s ease',
    });
    document.body.appendChild(toast);

    // Fade in
    requestAnimationFrame(() => { toast.style.opacity = '1'; });

    // Fade out and remove after 3s
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then((registration) => {
      // Listen for new service worker installing
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener('statechange', () => {
          // New SW activated and controlling the page
          if (newWorker.state === 'activated' && navigator.serviceWorker.controller) {
            showUpdateToast('App updated — you have the latest version');
          }
        });
      });

      // Periodic update check
      setInterval(() => {
        registration.update().catch(() => {
          // Network error during update check — not critical
        });
      }, UPDATE_CHECK_INTERVAL);
    }).catch(() => {
      // Service worker registration failed — not critical
    });

    // Listen for messages from service worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'SW_UPDATED') {
        showUpdateToast('App updated — you have the latest version');
      }
    });
  });
}
